
import { useState } from 'react';

const WhitelabelSettings = () => {
  const [activeTemplate, setActiveTemplate] = useState('default');
  const [globalSettings, setGlobalSettings] = useState({
    defaultLogo: '',
    defaultPrimaryColor: '#2563eb',
    defaultSecondaryColor: '#64748b',
    defaultFontFamily: 'Inter',
    allowCustomDomains: true,
    requireSSL: true,
    enableAnalytics: true,
    showPoweredBy: false
  });

  const templates = [
    {
      id: 'default',
      name: 'Default Template',
      description: 'Clean and professional design suitable for most businesses',
      preview: 'https://public.readdy.ai/ai/img_res/44583353a32eb44eb5a0706d34c2bc58.jpg',
      features: ['Responsive Design', 'Dark Mode', 'Custom Branding', 'Mobile Optimized']
    },
    {
      id: 'construction',
      name: 'Construction Template',
      description: 'Rugged design perfect for construction and industrial companies',
      preview: 'https://readdy.ai/api/search-image?query=construction%20industry%20dashboard%20interface%2C%20orange%20and%20black%20colors%2C%20industrial%20design%2C%20project%20management%20layout&width=300&height=200&seq=2&orientation=landscape',
      features: ['Project Tracking', 'Safety Features', 'Equipment Management', 'Site Photos']
    },
    {
      id: 'creative',
      name: 'Creative Template',
      description: 'Vibrant and artistic design for creative agencies and studios',
      preview: 'https://readdy.ai/api/search-image?query=creative%20agency%20dashboard%20interface%2C%20colorful%20gradient%20design%2C%20artistic%20layout%2C%20portfolio%20showcase&width=300&height=200&seq=3&orientation=landscape',
      features: ['Portfolio Gallery', 'Creative Tools', 'Client Showcase', 'Design Assets']
    },
    {
      id: 'minimal',
      name: 'Minimal Template',
      description: 'Ultra-clean minimalist design for modern businesses',
      preview: 'https://readdy.ai/api/search-image?query=minimalist%20dashboard%20interface%2C%20white%20and%20gray%20colors%2C%20clean%20typography%2C%20simple%20elegant%20design&width=300&height=200&seq=4&orientation=landscape',
      features: ['Minimal UI', 'Focus Mode', 'Clean Typography', 'Distraction-free']
    }
  ];

  const customizationOptions = [
    {
      category: 'Branding',
      options: [
        { id: 'logo', name: 'Custom Logo Upload', description: 'Allow companies to upload their own logos' },
        { id: 'colors', name: 'Color Customization', description: 'Enable primary and secondary color changes' },
        { id: 'fonts', name: 'Font Selection', description: 'Provide font family options' },
        { id: 'favicon', name: 'Custom Favicon', description: 'Allow custom favicon uploads' }
      ]
    },
    {
      category: 'Layout',
      options: [
        { id: 'sidebar', name: 'Sidebar Customization', description: 'Allow sidebar layout modifications' },
        { id: 'header', name: 'Header Customization', description: 'Enable header layout changes' },
        { id: 'dashboard', name: 'Dashboard Widgets', description: 'Customizable dashboard components' },
        { id: 'navigation', name: 'Navigation Menu', description: 'Custom navigation structure' }
      ]
    },
    {
      category: 'Features',
      options: [
        { id: 'modules', name: 'Module Selection', description: 'Choose which features to enable' },
        { id: 'integrations', name: 'Third-party Integrations', description: 'Enable external service connections' },
        { id: 'permissions', name: 'Role Permissions', description: 'Customizable user role settings' },
        { id: 'workflows', name: 'Custom Workflows', description: 'Define business process flows' }
      ]
    }
  ];

  const handleSaveSettings = () => {
    console.log('Saving whitelabel settings:', globalSettings);
  };

  const handleTemplateSelect = (templateId: string) => {
    setActiveTemplate(templateId);
    console.log('Selected template:', templateId);
  };

  return (
    <div className="space-y-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Whitelabel Settings</h2>
        <p className="text-gray-600">Configure global branding and customization options for all company platforms</p>
      </div>

      {/* Global Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Global Configuration</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Primary Color
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={globalSettings.defaultPrimaryColor}
                  onChange={(e) => setGlobalSettings(prev => ({ ...prev, defaultPrimaryColor: e.target.value }))}
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={globalSettings.defaultPrimaryColor}
                  onChange={(e) => setGlobalSettings(prev => ({ ...prev, defaultPrimaryColor: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Secondary Color
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={globalSettings.defaultSecondaryColor}
                  onChange={(e) => setGlobalSettings(prev => ({ ...prev, defaultSecondaryColor: e.target.value }))}
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={globalSettings.defaultSecondaryColor}
                  onChange={(e) => setGlobalSettings(prev => ({ ...prev, defaultSecondaryColor: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Font Family
              </label>
              <select
                value={globalSettings.defaultFontFamily}
                onChange={(e) => setGlobalSettings(prev => ({ ...prev, defaultFontFamily: e.target.value }))}
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              >
                <option value="Inter">Inter</option>
                <option value="Roboto">Roboto</option>
                <option value="Open Sans">Open Sans</option>
                <option value="Lato">Lato</option>
                <option value="Montserrat">Montserrat</option>
                <option value="Poppins">Poppins</option>
              </select>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Allow Custom Domains</label>
                <p className="text-xs text-gray-500">Enable companies to use their own domains</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={globalSettings.allowCustomDomains}
                  onChange={(e) => setGlobalSettings(prev => ({ ...prev, allowCustomDomains: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Require SSL</label>
                <p className="text-xs text-gray-500">Force HTTPS for all custom domains</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={globalSettings.requireSSL}
                  onChange={(e) => setGlobalSettings(prev => ({ ...prev, requireSSL: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Enable Analytics</label>
                <p className="text-xs text-gray-500">Include usage analytics for companies</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={globalSettings.enableAnalytics}
                  onChange={(e) => setGlobalSettings(prev => ({ ...prev, enableAnalytics: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Show "Powered By"</label>
                <p className="text-xs text-gray-500">Display platform branding in footer</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={globalSettings.showPoweredBy}
                  onChange={(e) => setGlobalSettings(prev => ({ ...prev, showPoweredBy: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Template Selection */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Library</h3>
        <p className="text-gray-600 mb-6">Choose default templates that companies can select from</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {templates.map((template) => (
            <div
              key={template.id}
              className={`border rounded-lg overflow-hidden cursor-pointer transition-all ${
                activeTemplate === template.id
                  ? 'border-red-500 ring-2 ring-red-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handleTemplateSelect(template.id)}
            >
              <img
                src={template.preview}
                alt={template.name}
                className="w-full h-32 object-cover"
              />
              <div className="p-4">
                <h4 className="font-medium text-gray-900 mb-1">{template.name}</h4>
                <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                <div className="space-y-1">
                  {template.features.map((feature, index) => (
                    <div key={index} className="flex items-center text-xs text-gray-500">
                      <i className="ri-check-line text-green-500 mr-1"></i>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Customization Options */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Customization Options</h3>
        <p className="text-gray-600 mb-6">Configure what customization features are available to companies</p>
        
        <div className="space-y-8">
          {customizationOptions.map((category) => (
            <div key={category.category}>
              <h4 className="text-md font-medium text-gray-900 mb-4">{category.category}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {category.options.map((option) => (
                  <div key={option.id} className="flex items-start justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <h5 className="text-sm font-medium text-gray-900">{option.name}</h5>
                      <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        defaultChecked={true}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveSettings}
          className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium whitespace-nowrap"
        >
          <i className="ri-save-line mr-2"></i>
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default WhitelabelSettings;
